import React, { useEffect } from 'react';
import { composer } from '../../../component-decorators/composer';
import { More, AddSmall, ArrowRightSmall, EditSmall, UnlinkSmall, LinkSmall, HiddenSmall, InfoCircleSmall, EmailSmall, DeleteSmall, FavoriteSmall, PaidPlansSmall, } from '@wix/wix-ui-icons-common';
import { appHasEditor, canUsePremiumApp, enableDeleteMLApp, getAppSettingsPageUrl, getEditorWebComps, isBundleApp, isBundleAppAndNotInstalled, isEmbeddedScriptApp, isPendingApp, isPermittedToDelete, isWixApp, openSettingsUrl, shouldShowAddAgain, wixChatBotLink, } from '../../../utils/appUtils';
import { EditorType, embeddedScriptAppStatus, Origin, } from '../../manage-installed-apps-page';
import { ModalNames } from '../../modals/modals';
import { IconButton, PopoverMenu } from '@wix/design-system';
import { manageAppsContextMenuAction, manageAppsDeletePendingAppClick, manageAppsOpenContextMenu, openAppClick, } from '@wix/bi-logger-market/v2';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useExperiments } from '@wix/wix-experiments-react';
import { useTranslation } from '../../../hooks/use-translation';
import { useHttpClient } from '../../../hooks/use-http-client';
import { useModal } from '../../../hooks/use-modal/use-modal';
import NewAppSettingsModal from '../../modals/app-settings-modal/new-app-settings-modal';
import { DeleteModal } from '../../modals/delete-modal/delete-modal';
import { SupportModal } from '../../modals/support-modal/support-modal';
export const ContextMenuButton = composer()
    .withPermissions()
    .compose(({ app, origin, msid, addAppAgain, getAppInstance, updateEmbeddedScriptAppsStatus, embeddedScriptAppsStatus, openModal, baseUrl, permissions, gotoWebSolutionPage, deleteApp, siteIsPremium, editorType, isActionRequiredApp, openApp, deleteAppFromBundle, bundleApps, isEditorless, locale, showUserActionNotification, editorUrl, authorizationHeader, openDeleteModalAppInEditor, }) => {
    const { experiments } = useExperiments();
    const biLogger = useBiLogger();
    const appId = app?.appId;
    const wasAppEverPublished = app?.appInfo?.wasEverPublished;
    const appSlug = app?.appInfo?.slug;
    const appInstanceId = app?.installationDetails?.instanceId;
    const appInstalledVersion = app?.installationDetails?.installedVersion;
    const supportEmails = app?.appInfo?.supportEmails;
    const companyId = app?.appInfo?.companyInfo?.id;
    const madeByWix = isWixApp(companyId);
    const isDayfulFlow = origin === Origin.DAYFUL;
    const [userReview, setUserReview] = React.useState(undefined); // eslint-disable-line
    const [isPlanChangeEnabled, setIsPlanChangeEnabled] = React.useState(false);
    const editReview = !!userReview;
    const t = useTranslation();
    const httpClient = useHttpClient();
    const modalController = useModal();
    const planChangeEndpoint = experiments?.enabled('specs.app-market.planChangeV2')
        ? 'plan-change-v2'
        : 'plan-change';
    function updateUserReview() {
        httpClient
            .get(`${baseUrl}/app-reviews/v1/reviews/user-review?appId=${appId}`, {
            withCredentials: true,
        })
            .then(({ data }) => setUserReview(data.userReview))
            .catch(() => { });
    }
    function PopoverMenuClick() {
        biLogger
            .report(manageAppsOpenContextMenu({
            appId,
            appInstanceId,
            market: origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                    ? 'adi_dashboard'
                    : 'dashboard'
                : origin,
            msid,
        }))
            .catch((e) => console.log(e));
        updateUserReview();
    }
    useEffect(() => {
        updateUserReview();
    }, []);
    useEffect(() => {
        httpClient
            .get(`https://${window.location.hostname}/_serverless/managed-installed-apps-serverless/${planChangeEndpoint}?appId=${appId}&metaSiteId=${msid}`)
            .then((response) => {
            return experiments?.enabled('specs.app-market.planChangeV2')
                ? response.data
                : response.data.isPlanChangeEnabled;
        })
            .then(setIsPlanChangeEnabled)
            .catch(() => { });
    }, [appId, msid]);
    if (isBundleAppAndNotInstalled(appId, bundleApps)) {
        return (React.createElement("div", { onClick: (e) => PopoverMenuClick() },
            React.createElement(PopoverMenu, { appendTo: "parent", placement: "left", dataHook: "manage-apps-context-menu", textSize: "small", triggerElement: React.createElement(IconButton, { size: "tiny", skin: "inverted", priority: "primary", dataHook: "manage-apps-more-button" },
                    React.createElement(More, null)) },
                React.createElement(PopoverMenu.MenuItem, { skin: "dark", key: "manage-apps-context-menu-dismiss-bundle", prefixIcon: React.createElement(HiddenSmall, null), dataHook: "manage-apps-manage-apps-context-menu-open-menu-dismiss-bundle", text: t('manage-apps.context-menu.hide'), onClick: () => {
                        sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'dismiss-bundle', editorType, biLogger);
                        dismissBundleApp(appId, deleteAppFromBundle);
                    } }))));
    }
    return (React.createElement("div", { onClick: (e) => PopoverMenuClick() },
        React.createElement(PopoverMenu, { appendTo: "parent", placement: "left", dataHook: "manage-apps-context-menu", textSize: "small", minWidth: "max-content", triggerElement: React.createElement(IconButton, { size: "tiny", skin: "inverted", priority: "primary", dataHook: "manage-apps-more-button" },
                React.createElement(More, null)) },
            !isEditorless && isActionRequiredApp && (React.createElement(PopoverMenu.MenuItem, { dataHook: "manage-apps-context-menu-open", prefixIcon: React.createElement(ArrowRightSmall, null), skin: "dark", text: t('manage-apps.context-menu.open'), onClick: async () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'open_app', editorType, biLogger);
                    biLogger
                        .report(openAppClick({
                        msid,
                        app_id: appId,
                        market: origin,
                        tag_type: 'manage_apps',
                        tag_name: 'manage_apps_app_row',
                    }))
                        .catch((e) => console.log(e));
                    await openApp(app);
                } })),
            getAppSettingsPageUrl(app) &&
                canUsePremiumApp(app, siteIsPremium) &&
                wasAppEverPublished && (React.createElement(PopoverMenu.MenuItem, { dataHook: "manage-apps-context-menu-setting-page-url", prefixIcon: React.createElement(EditSmall, null), skin: "dark", text: t('manage-apps.context-menu.edit-script'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'edit_settings', editorType, biLogger);
                    const settingsUrl = openSettingsUrl(app, embeddedScriptAppsStatus, getAppInstance, openModal);
                    modalController.open(React.createElement(NewAppSettingsModal, { settingsUrl: settingsUrl, closeModal: modalController.close }));
                } })),
            isEmbeddedScriptApp(app) &&
                canUsePremiumApp(app, siteIsPremium) &&
                wasAppEverPublished && (React.createElement(PopoverMenu.MenuItem, { dataHook: "manage-apps-context-menu-embedded-toggle", prefixIcon: getSmallEmbeddedIcon(appId, embeddedScriptAppsStatus), skin: "dark", text: t(getNewEmbeddedKey(appId, embeddedScriptAppsStatus)), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, `embedded_script`, editorType, biLogger);
                    const settingsUrl = toggleEmbeddedStatus(app, embeddedScriptAppsStatus, updateEmbeddedScriptAppsStatus, getAppInstance, openModal);
                    if (settingsUrl !== undefined) {
                        modalController.open(React.createElement(NewAppSettingsModal, { settingsUrl: settingsUrl, closeModal: modalController.close }));
                    }
                } })),
            !!shouldShowAddAgain(app, origin) && wasAppEverPublished && (React.createElement(PopoverMenu.MenuItem, { skin: "dark", dataHook: "manage-apps-context-menu-add-again", prefixIcon: React.createElement(AddSmall, null), text: t('manage-apps.context-menu.add-again'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'add_again', editorType, biLogger);
                    addAppAgain(appId, appInstalledVersion);
                } })),
            !isEditorless && wasAppEverPublished && (React.createElement(PopoverMenu.MenuItem, { skin: "dark", dataHook: "manage-apps-context-menu-app-info", prefixIcon: React.createElement(InfoCircleSmall, null), text: t('manage-apps.context-menu.app-info'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'app-info', editorType, biLogger);
                    if (isDayfulFlow) {
                        openMinifiedAppPage(app, openModal);
                    }
                    else {
                        gotoWebSolutionPage(appSlug);
                    }
                } })),
            !isEditorless &&
                !isPendingApp(app) &&
                !editReview &&
                wasAppEverPublished && (React.createElement(PopoverMenu.MenuItem, { skin: "dark", dataHook: "manage-apps-context-menu-rate", prefixIcon: React.createElement(FavoriteSmall, null), text: t('manage-apps.context-menu.rate-review'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, `rate_app`, editorType, biLogger);
                    openPostRateModal(app, openModal);
                } })),
            !isPendingApp(app) && editReview && wasAppEverPublished && (React.createElement(PopoverMenu.MenuItem, { skin: "dark", dataHook: "manage-apps-context-menu-edit-review", prefixIcon: React.createElement(FavoriteSmall, null), text: t('manage-apps.context-menu.edit-review'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, `edit_review`, editorType, biLogger);
                    openEditRateModal(app, openModal);
                } })),
            !isEditorless && wasAppEverPublished && (React.createElement(PopoverMenu.Divider, { key: "manage-apps-context-menu-support-mail-divider" })),
            madeByWix || !!supportEmails?.length ? (React.createElement(PopoverMenu.MenuItem, { skin: "dark", key: "manage-apps-context-menu-support-mail", prefixIcon: React.createElement(EmailSmall, null), dataHook: "manage-apps-context-menu-support-mail", text: madeByWix
                    ? t('manage-apps.context-menu.support-title-wix')
                    : t('manage-apps.context-menu.support-title'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'contact_us', editorType, biLogger);
                    const madeByWix = sentSupportMail(app, origin, msid, openModal, experiments);
                    if (!madeByWix) {
                        modalController.open(React.createElement(SupportModal, { app: app, msId: msid, origin: origin, closeModal: modalController.close, showUserActionNotification: showUserActionNotification, authorizationHeader: authorizationHeader, baseUrl: baseUrl, editorType: editorType }));
                    }
                } })) : null,
            isBundleApp(appId, bundleApps) && (React.createElement(PopoverMenu.MenuItem, { skin: "dark", key: "manage-apps-context-menu-dismiss-bundle", prefixIcon: React.createElement(HiddenSmall, null), dataHook: "manage-apps-context-menu-dismiss-bundle", text: t('manage-apps.context-menu.hide'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'dismiss-bundle', editorType, biLogger);
                    dismissBundleApp(appId, deleteAppFromBundle);
                } })),
            isEasyteamAppId(appId) ||
                experiments?.enabled('specs.app-market.TpaPlanDowngrade') ? (React.createElement(PopoverMenu.MenuItem, { dataHook: "manage-apps-context-menu-change-pricing-plan", prefixIcon: React.createElement(PaidPlansSmall, null), disabled: isPlanChangeEnabled ? false : true, text: t('manage-apps-context-menu-change-plan'), subtitle: isPlanChangeEnabled
                    ? null
                    : t('manage-apps-context-menu-change-plan-disabled.subtitle'), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'change-pricing-plans', editorType, biLogger);
                    window.open(`https://manage.wix.com/app-pricing-plans/${appId}/plan-change?meta-site-id=${msid}&origin=manage_apps_bizmgr`, '_blank');
                } })) : null,
            React.createElement(PopoverMenu.MenuItem, { disabled: !isPermittedToDelete(app, permissions), dataHook: "manage-apps-context-menu-delete", prefixIcon: React.createElement(DeleteSmall, null), skin: "dark", text: t(getDeleteTextKey(app, experiments, editorType)), onClick: () => {
                    sendContextMenuActionBi(appId, appInstanceId, origin, msid, 'delete_app', editorType, biLogger);
                    const isDeleted = handleDeleteApp(app, openModal, origin, msid, deleteApp, editorType, isEditorless, biLogger);
                    if (!isDeleted) {
                        modalController.open(React.createElement(DeleteModal, { app: app, msId: msid, origin: origin, editorUrl: editorUrl, deleteApp: deleteApp, closeModal: modalController.close, editorType: editorType, baseUrl: baseUrl, authorizationHeader: authorizationHeader, openDeleteModalAppInEditor: openDeleteModalAppInEditor }));
                    }
                } }))));
});
// ------------------------------HELPER-FUNCTIONS------------------------------
function sentSupportMail(app, origin, msId, openModal, experiments) {
    const companyId = app?.appInfo?.companyInfo?.id;
    const madeByWix = isWixApp(companyId);
    if (madeByWix) {
        window.open(wixChatBotLink);
        return true;
    }
    else {
        return false;
    }
}
function dismissBundleApp(appId, deleteAppFromBundle) {
    localStorage.setItem(`dismissed-${appId}`, 'dismissed');
    deleteAppFromBundle(appId);
}
function getSmallEmbeddedIcon(appDefId, status) {
    const appStatus = status && status[appDefId]?.status;
    return appStatus === embeddedScriptAppStatus.ENABLED ? (React.createElement(UnlinkSmall, null)) : (React.createElement(LinkSmall, null));
}
function getNewEmbeddedKey(appDefId, status) {
    const appStatus = status && status[appDefId]?.status;
    return appStatus === embeddedScriptAppStatus.ENABLED
        ? 'manage-apps.context-menu.pause'
        : 'manage-apps.context-menu.resume';
}
function toggleEmbeddedStatus(app, embeddedScriptAppsStatus, updateEmbeddedScriptAppsStatus, getAppInstance, openModal) {
    const appId = app?.appId;
    const appInstanceId = app?.installationDetails?.instanceId;
    const status = embeddedScriptAppsStatus[appId].status || embeddedScriptAppStatus.DISABLED;
    if (status === embeddedScriptAppStatus.ENABLED) {
        updateEmbeddedScriptAppsStatus(appId, embeddedScriptAppStatus.DISABLED, appInstanceId, embeddedScriptAppsStatus);
    }
    else if (status === embeddedScriptAppStatus.DISABLED) {
        updateEmbeddedScriptAppsStatus(appId, embeddedScriptAppStatus.ENABLED, appInstanceId, embeddedScriptAppsStatus);
    }
    else if (getAppSettingsPageUrl(app)) {
        const settingsUrl = openSettingsUrl(app, embeddedScriptAppsStatus, getAppInstance, openModal);
        return settingsUrl;
    }
    return undefined;
}
function getDeleteTextKey(app, experiments, editorType) {
    const isAppPending = isPendingApp(app);
    const appId = app?.appId;
    if (!appHasEditor(app) ||
        getEditorWebComps(app).length > 0 ||
        isAppPending ||
        (editorType !== EditorType.ADI &&
            appId !== '14d84998-ae09-1abf-c6fc-3f3cace5bf19') ||
        enableDeleteMLApp(appId, experiments)) {
        return 'manage-apps.context-menu.delete';
    }
    return 'manage-apps.context-menu.how-to-delete';
}
function handleDeleteApp(app, openModal, origin, msid, deleteApp, editorType, isEditorless, biLogger) {
    const appId = app?.appId;
    const appInstanceId = app?.installationDetails?.instanceId;
    if (isEditorless || isPendingApp(app)) {
        biLogger
            .report(manageAppsDeletePendingAppClick({
            appId,
            appInstanceId,
            market: origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                    ? 'adi_dashboard'
                    : 'dashboard'
                : origin,
            msid,
        }))
            .catch((e) => console.log(e));
        deleteApp(app, false);
        return true;
    }
    else {
        return false;
    }
}
function openPostRateModal(app, openModal) {
    openModal(ModalNames.RATE_APP, { app });
}
function openMinifiedAppPage(app, openModal) {
    openModal(ModalNames.IN_CONTEXT_INSTALLATION, { app });
}
function openEditRateModal(app, openModal) {
    openModal(ModalNames.EDIT_REVIEW, { app });
}
function sendContextMenuActionBi(appId, appInstanceId, origin, msid, contextAction, editorType, biLogger) {
    biLogger
        .report(manageAppsContextMenuAction({
        appId,
        appInstanceId,
        contextAction,
        market: origin === Origin.BIZMGR
            ? editorType === EditorType.ADI
                ? 'adi_dashboard'
                : 'dashboard'
            : origin,
        msid,
    }))
        .catch((e) => console.log(e));
}
function isEasyteamAppId(id) {
    return id === 'aacd9f4f-b085-4348-9aed-2ad5e7768076';
}
//# sourceMappingURL=context-menu-button.js.map